import { useRef, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { SearchSelect } from '../../components/SearchSelect';
import { DateInput, Field, FormGrid, NumberInput, TextArea, TextInput } from '../../components/ui';
import type { Item } from '../../lib/types';
import type { PriceListItem, PriceListRow } from './PriceListsPage';

// 表单内一行价格明细：字符串承载输入，保存时转数字。
// 有 id 者为库中既有行（就地更新并保留 uom/币种/客户/有效期等导入字段），
// 无 id 者为新增行。
interface LineForm {
  key: string;
  id?: string;
  item_id: string | null;
  item_code: string;
  item_name: string;
  unit_price: string;
  min_quantity: string;
  // 只读展示：既有行携带的导入元数据（不参与提交，服务端就地更新时保留）
  uom?: string;
  legacy?: boolean;
}

export interface PriceListEditorProps {
  // 传入已存在的价目表即为编辑模式；null 为新建。
  initial?: PriceListRow | null;
  onCancel: () => void;
  onSaved: (saved: PriceListRow) => void;
}

function lineFromRecord(l: PriceListItem, key: string): LineForm {
  return {
    key,
    id: l.id,
    item_id: l.item_id ?? null,
    item_code: l.item_code || '',
    item_name: l.item_name || '',
    unit_price: l.unit_price == null ? '' : String(l.unit_price),
    min_quantity: l.min_quantity == null ? '1' : String(l.min_quantity),
    uom: l.uom,
    legacy: !!l.legacy_erpnext_id,
  };
}

export function PriceListEditor({ initial, onCancel, onSaved }: PriceListEditorProps) {
  const isEdit = !!initial?.id;
  const seq = useRef(1);
  const nextKey = () => `L${seq.current++}`;

  const [name, setName] = useState(initial?.name ?? '');
  const [validFrom, setValidFrom] = useState(initial?.valid_from?.slice(0, 10) ?? '');
  const [validUntil, setValidUntil] = useState(initial?.valid_until?.slice(0, 10) ?? '');
  const [active, setActive] = useState(initial ? initial.active === 1 : true);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [lines, setLines] = useState<LineForm[]>(() => {
    const src = initial?.lines;
    if (src && src.length) return src.map((l) => lineFromRecord(l, nextKey()));
    return [{ key: nextKey(), item_id: null, item_code: '', item_name: '', unit_price: '', min_quantity: '1' }];
  });

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fieldErr, setFieldErr] = useState<string | null>(null);

  const patchLine = (key: string, patch: Partial<LineForm>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const applyItem = (key: string, item: Item | null) => {
    if (!item) { patchLine(key, { item_id: null, item_code: '', item_name: '' }); return; }
    setLines((prev) => prev.map((l) => {
      if (l.key !== key) return l;
      const next: LineForm = { ...l, item_id: item.id, item_code: item.code, item_name: item.name_es || item.name_zh || '' };
      // 新增行且未填价时，用物料售价预填，减少手工输入。
      if (!l.unit_price.trim() && item.sale_price != null) next.unit_price = String(item.sale_price);
      if (!l.uom && item.stock_uom) next.uom = item.stock_uom;
      return next;
    }));
  };

  const addLine = () =>
    setLines((prev) => [...prev, { key: nextKey(), item_id: null, item_code: '', item_name: '', unit_price: '', min_quantity: '1' }]);

  const removeLine = (key: string) => setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.key !== key) : prev));

  const validate = (): boolean => {
    if (!name.trim()) { setFieldErr('价目表名称为必填'); return false; }
    if (!lines.length) { setFieldErr('至少需要一个价格明细'); return false; }
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.item_id) { setFieldErr(`第 ${i + 1} 行：请选择物料`); return false; }
      const price = Number(l.unit_price);
      if (l.unit_price.trim() === '' || !Number.isFinite(price) || price < 0) { setFieldErr(`第 ${i + 1} 行：单价需为非负数字`); return false; }
      const moq = Number(l.min_quantity || '1');
      if (!Number.isFinite(moq) || moq <= 0) { setFieldErr(`第 ${i + 1} 行：起订量需大于 0`); return false; }
    }
    setFieldErr(null);
    return true;
  };

  const save = async () => {
    setErr(null);
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        currency: 'USD',
        valid_from: validFrom || null,
        valid_until: validUntil || null,
        active,
        notes: notes || null,
        lines: lines.map((l) => ({
          id: l.id,                       // 既有行携带 id → 就地更新；新增行为 undefined
          item_id: l.item_id,
          unit_price: Number(l.unit_price),
          min_quantity: Number(l.min_quantity || '1'),
        })),
      };
      const res = isEdit
        ? await api.put<{ data: PriceListRow }>(`/api/price-lists/${initial!.id}`, payload)
        : await api.post<{ data: PriceListRow }>('/api/price-lists', payload);
      onSaved(res.data);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '保存失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head">
        <div style={{ minWidth: 0 }}>
          <h2>{isEdit ? `编辑价目表 · ${initial!.name}` : '新建价目表'}</h2>
          <div className="desc">
            {isEdit ? '修改表头与价格明细 · PUT /api/price-lists/:id（既有明细就地更新，保留导入的单位/客户/有效期）' : 'POST /api/price-lists · 销售价格单固定 USD 计价'}
          </div>
        </div>
        <div className="spacer" />
        <span className="muted" style={{ fontSize: 12 }}>{lines.length} 条明细</span>
      </div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}

        <FormGrid>
          <Field label="价目表名称" required span>
            <TextInput value={name} placeholder="如 Standard Selling" onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="生效日期"><DateInput value={validFrom} onChange={(e) => setValidFrom(e.target.value)} /></Field>
          <Field label="失效日期" hint="留空则不设上限"><DateInput value={validUntil} onChange={(e) => setValidUntil(e.target.value)} /></Field>
          <Field label="币种" hint="固定 USD"><TextInput value="USD" disabled readOnly /></Field>
          <Field label="状态">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} style={{ width: 16, height: 16 }} />
              <span>{active ? '启用' : '停用'}</span>
            </label>
          </Field>
          <Field label="备注" span>
            <TextArea value={notes} placeholder="内部备注…" onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </FormGrid>

        {fieldErr ? <div className="notice warn" style={{ margin: '12px 0 0' }}>{fieldErr}</div> : null}

        <div style={{ marginTop: 16 }}>
          <div className="detail-head" style={{ marginBottom: 8 }}>
            <h3 style={{ fontSize: 14 }}>价格明细</h3>
            <div className="spacer" />
            <button className="btn" onClick={addLine} type="button">+ 添加明细</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="line-grid">
              <thead>
                <tr>
                  <th style={{ minWidth: 240 }}>物料</th>
                  <th style={{ width: 120 }} className="col-r">单价 (USD)</th>
                  <th style={{ width: 110 }} className="col-r">起订量</th>
                  <th style={{ width: 90 }}>单位</th>
                  <th style={{ width: 96 }}>来源</th>
                  <th style={{ width: 34 }} aria-label="操作" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.key}>
                    <td>
                      <SearchSelect<Item>
                        endpoint="/api/items"
                        value={l.item_id}
                        label={l.item_code ? `${l.item_code} · ${l.item_name}` : (l.item_name || '')}
                        limit={15}
                        ariaLabel="选择物料"
                        placeholder="编码 / 中英文名…"
                        optionTitle={(i) => i.name_es || i.name_zh}
                        optionMeta={(i) => i.code}
                        onChange={(it) => applyItem(l.key, it)}
                      />
                    </td>
                    <td><NumberInput value={l.unit_price} min={0} step="0.01" onChange={(e) => patchLine(l.key, { unit_price: e.target.value })} /></td>
                    <td><NumberInput value={l.min_quantity} min={1} step="1" onChange={(e) => patchLine(l.key, { min_quantity: e.target.value })} /></td>
                    <td className="muted">{l.uom || '—'}</td>
                    <td className="muted">{l.legacy ? 'ERPNext' : (l.id ? '系统内建' : '新增')}</td>
                    <td><button type="button" className="row-del" title="删除该行" onClick={() => removeLine(l.key)}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="detail-actions" style={{ marginTop: 16 }}>
          <button className="btn primary" onClick={save} disabled={busy}>{busy ? '保存中…' : isEdit ? '保存修改' : '创建价目表'}</button>
          <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
          <span className="muted" style={{ fontSize: 11.5, marginLeft: 6 }}>删除明细行将在保存时从库中移除；既有行的单位/客户/有效期等导入信息会被保留。</span>
        </div>
      </div>
    </section>
  );
}
